import Button from '@/components/elements/forms/buttons/Button/Button';
import Icon from '@/components/icons/Icon';
import { classnames } from '@/helpers/general';
import { _t } from '@/locale';
import { SelectionSummaryProps } from './SelectionSummary.types';

const baseTranslationKey = 'features.bookingSelection.SelectionSummary' as const;

const AddLink = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <Button
    variant="link"
    size="md"
    inline
    className="py-sm"
    label={label}
    rightIcon={<Icon variant="plus-circle" color="fg_link" style={{ width: 20, height: 20 }} />}
    onClick={onClick}
  />
);

/**
 * The "Valda tjänster / Totalt / Sök tider" selection summary — the desktop side column
 * of the /tillval step page (KTD8: the page shell exposes a generic sideColumn slot; this
 * is what /tillval puts in it). Presentational, props-driven, no Redux — pricing is
 * resolved by the caller via getSelectionPricing / getServicePrice so the numbers match
 * SelectionsBar and the calendar.
 */
const SelectionSummary = ({
  items,
  total,
  onRemove,
  onAddServices,
  onAddRecommended,
  onSearchTimes,
}: SelectionSummaryProps) => (
  <div className="bg-surface_l2 border-border_regular p-xl gap-md flex flex-col rounded-lg border">
    <h2 className="text-fg_primary text-xl font-bold">{_t(`${baseTranslationKey}.title`)}</h2>
    <ul className="border-border_regular flex flex-col border-t">
      {items.map((item) => {
        // Only service rows carry a numeric id, and only they are removable.
        const removeId = item.removable && typeof item.id === 'number' ? item.id : undefined;

        return (
          <li key={item.id} className="border-border_regular py-sm flex items-start gap-[18px] border-b">
            <div className="gap-xs flex min-w-0 flex-1 flex-col items-start">
              <span className="text-l text-fg_primary w-full truncate">{item.name}</span>
              {item.duration || (removeId !== undefined && onRemove) ? (
                <div className="gap-sm flex w-full items-start">
                  {item.duration ? <span className="text-m text-fg_secondary">{item.duration}</span> : null}
                  {removeId !== undefined && onRemove ? (
                    <button
                      type="button"
                      className="text-m text-fg_negative underline"
                      aria-label={_t(`${baseTranslationKey}.removeLabel`, { name: item.name })}
                      onClick={() => onRemove(removeId)}>
                      {_t(`${baseTranslationKey}.remove`)}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            {item.price ? (
              <div className="flex shrink-0 flex-col items-end">
                <span
                  className={classnames(
                    'text-l text-right',
                    item.originalPrice ? 'text-fg_highlight' : 'text-fg_primary',
                  )}>
                  {item.price}
                </span>
                {item.originalPrice ? (
                  <span className="text-m text-fg_secondary text-right line-through">{item.originalPrice}</span>
                ) : null}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
    {onAddServices || onAddRecommended ? (
      <div className="flex flex-col items-start">
        {onAddServices ? <AddLink label={_t('add-more-services')} onClick={onAddServices} /> : null}
        {onAddRecommended ? <AddLink label={_t('recommendedServices')} onClick={onAddRecommended} /> : null}
      </div>
    ) : null}
    {total ? (
      <div className="bg-bg_secondary_neutral px-md flex items-center justify-between rounded-sm py-[18px]">
        <span className="text-fg_primary min-w-0 flex-1 truncate text-xl font-bold">{_t('total')}</span>
        <span className="text-fg_primary shrink-0 text-right text-xl font-bold">{total}</span>
      </div>
    ) : null}
    {onSearchTimes ? (
      <Button
        variant="primary"
        size="lg"
        block
        label={_t(`${baseTranslationKey}.cta.searchTimes`)}
        leftIcon={<Icon variant="search" size="xs" color="fg_on_primary" />}
        onClick={onSearchTimes}
      />
    ) : null}
  </div>
);

export default SelectionSummary;
